<template>
  <form method="post" @submit.prevent="onSend" novalidate>
    <form-wrapper :validator="$v.form">
      <div class="columns">
        <div class="column is-4 pt-5">
          <form-group name="is_public">
            <b-switch v-model="form.is_public" left-label disabled>
              {{ $t('attributes.gift_application_public') }}
            </b-switch>
          </form-group>
        </div>

        <div class="column is-8">
          <div class="buttons is-right">
            <b-button
              :loading="sending"
              style="width: 100px"
              @click="onCancel"
              tabindex="10"
            >
              {{ $t('attributes.cancel') }}
            </b-button>
            <b-button
              :loading="sending"
              type="is-primary"
              native-type="submit"
              style="width: 100px"
              tabindex="10"
            >
              {{ $t('attributes.confirm') }}
            </b-button>
          </div>
        </div>
      </div>

      <form-group
        v-if="isFit"
        name="user_id"
        attribute="attributes.gift_user"
        :label="$t('attributes.gift_user')"
        label-position="on-border"
      >
        <b-input v-model="form.user_info" name="user_id" disabled></b-input>
      </form-group>

      <div v-if="isGroup" class="columns mb-1">
        <div class="column">
          <form-group
            name="group_code"
            attribute="attributes.gift_group"
            :label="$t('attributes.gift_group')"
            label-position="on-border"
          >
            <b-input
              :value="form.group_code"
              name="group_code"
              disabled
            ></b-input>
          </form-group>
        </div>

        <div class="column">
          <form-group
            name="group_departure_date"
            attribute="attributes.departure_date"
            :label="$t('attributes.departure_date')"
            label-position="on-border"
          >
            <b-input
              v-model="form.group_departure_date"
              name="group_departure_date"
              disabled
            ></b-input>
          </form-group>
        </div>
      </div>

      <form-group
        v-if="isGroup"
        name="group_name"
        attribute="attributes.gift_group"
        :label="$t('attributes.group_name')"
        label-position="on-border"
      >
        <b-input v-model="form.group_name" name="group_name" disabled></b-input>
      </form-group>

      <form-group
        name="app_remark"
        :label="$t('attributes.gift_application_remark')"
        label-position="on-border"
      >
        <b-input
          v-model="form.app_remark"
          name="remark"
          maxlength="500"
          :has-counter="false"
          expanded
          disabled
        ></b-input>
      </form-group>

      <form-group
        name="remark"
        :label="$t('attributes.gift_review_return_reason')"
        label-position="on-border"
      >
        <b-input
          v-model="form.remark"
          tabindex="1"
          name="remark"
          maxlength="500"
          :has-counter="false"
          :placeholder="
            $t('message.please_input') +
              $t('attributes.gift_review_return_reason')
          "
          expanded
        ></b-input>
      </form-group>

      <div>
        <div class="columns mb-0">
          <p
            class="column is-5 pb-1 is-size-5 has-text-weight-semibold has-text-warning-dark"
          >
            已申請贈品列表
          </p>
          <p
            class="column is-size-5 pb-1 has-text-weight-semibold has-text-warning-dark"
          >
            {{ $t('attributes.gift_application_return_quantity') }}:
            {{ calculateTotalReturnQuantity() }}
          </p>
          <p
            class="column is-size-5 pb-1 has-text-weight-semibold has-text-warning-dark"
          >
            {{ $t('attributes.gift_application_return_amount') }}:
            {{ formatCurrency(calculateTotalReturnAmount()) }}
          </p>
        </div>
        <b-table
          :data="form.items"
          :mobile-cards="false"
          narrowed
          sticky-header
          height="calc(100vh - 530px)"
        >
          <b-table-column
            field="name"
            :label="$t('attributes.name')"
            header-class="nowrap"
            v-slot="props"
          >
            {{ props.row.gift_full_name }}
          </b-table-column>

          <b-table-column
            field="net_price"
            :label="$t('attributes.gift_net_price')"
            numeric
            header-class="nowrap"
            v-slot="props"
          >
            <span :class="{ 'is-strike': isFree(props.row) }">
              {{ props.row.net_price }}
            </span>
          </b-table-column>

          <b-table-column
            field="origin_quantity"
            :label="$t('attributes.gift_application_request_quantity')"
            numeric
            header-class="nowrap"
            v-slot="props"
          >
            {{ props.row.origin_quantity }}
          </b-table-column>
          <b-table-column
            field="quantity"
            :label="$t('attributes.gift_application_return_quantity')"
            numeric
            header-class="nowrap"
            v-slot="props"
          >
            <b-input
              v-model.number="props.row.quantity"
              tabindex="2"
              name="quantity"
              type="number"
              min="0"
              :max="props.row.origin_quantity"
              rounded
              :placeholder="
                $t('message.please_input') +
                  $t('attributes.gift_application_return_quantity')
              "
            ></b-input>
          </b-table-column>
          <b-table-column
            field="fina_quantity"
            :label="$t('attributes.gift_application_final_quantity')"
            numeric
            header-class="nowrap"
            v-slot="props"
          >
            {{ props.row.origin_quantity - props.row.quantity }}
          </b-table-column>
          <b-table-column
            field="origin_amount"
            :label="$t('attributes.gift_application_apply_amount')"
            numeric
            header-class="nowrap"
            v-slot="props"
          >
            {{ formatCurrency(props.row.origin_amount) }}
          </b-table-column>
          <b-table-column
            field="amount"
            :label="$t('attributes.gift_application_return_amount')"
            numeric
            header-class="nowrap"
            v-slot="props"
          >
            {{ formatCurrency(calculateReturnAmount(props.row)) }}
          </b-table-column>
        </b-table>
      </div>
    </form-wrapper>
  </form>
</template>

<script>
import { GIFT_REVIEW_APPLY_TYPES } from '@/utils/constants'
import { maxLength, required } from 'vuelidate/lib/validators'
import notifyService from '@/services/notifyService'

export default {
  name: 'ApplicationReturnForm',

  props: {
    type: {
      type: String,
      required: true,
      validator(value) {
        return [
          GIFT_REVIEW_APPLY_TYPES.FIT,
          GIFT_REVIEW_APPLY_TYPES.GROUP
        ].includes(value)
      }
    },
    sales_code: {
      type: String,
      required: true
    },
    sales_name: {
      type: String,
      required: true
    },
    sales_department: {
      type: String,
      required: true
    },
    application: {
      type: Object,
      required: true
    },
    sending: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      isFit: this.type === GIFT_REVIEW_APPLY_TYPES.FIT,
      isGroup: this.type === GIFT_REVIEW_APPLY_TYPES.GROUP,

      form: this.extractForm(this.application)
    }
  },
  watch: {
    application(application) {
      this.form = this.extractForm(application)
      this.$v.$reset()
    }
  },
  validations() {
    return {
      form: {
        remark: { required, maxLength: maxLength(500) }
      }
    }
  },
  methods: {
    extractForm(application) {
      return {
        application_id: application.application_id,
        is_public: application.is_public,
        user_id: application.user_id,
        user_name: application.user_name,
        user_identity: application.user_identity,
        user_phone: application.user_phone,
        user_info: application.user_info,
        group_code: application.group_code,
        group_name: application.group_name,
        group_departure_date: application.group_departure_date,
        items: application.items.map(item => ({ ...item })),
        origin_quantity: application.origin_quantity,
        quantity: application.quantity,
        final_quantity: application.final_quantity,
        origin_amount: application.origin_amount,
        amount: application.amount,
        app_remark: application.app_remark,
        remark: application.remark
      }
    },
    onCancel() {
      this.$emit('cancel')
    },
    async onSend() {
      for (let item of this.form.items) {
        if (item.quantity === '') {
          item.quantity = +item.quantity
        }
      }

      const items = this.form.items.filter(
        t => t.quantity < 0 || t.quantity > t.origin_quantity
      )

      if (items.length > 0) {
        const item = items[0]
        const message = `${item.gift_full_name} ${
          item.quantity < 0
            ? '退回數量不可小於零！'
            : '退回數量超過申請庫存數量！'
        }`
        return notifyService.error(message)
      }

      this.form.items.forEach(item => {
        item.final_quantity = item.origin_quantity - item.quantity
        item.amount = this.calculateReturnAmount(item)
      })

      this.form.quantity = this.calculateTotalReturnQuantity()
      this.form.final_quantity = this.form.origin_quantity - this.form.quantity
      this.form.amount = this.calculateTotalReturnAmount()

      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    },
    isFree(item) {
      return (
        this.application.is_public ||
        item.gift.department_no === this.sales_department
      )
    },
    calculateReturnAmount(item) {
      if (item.quantity < 0) {
        return 0
      }

      return this.isFree(item) ? 0 : item.net_price * +item.quantity
    },
    calculateTotalReturnQuantity() {
      return this.form.items.reduce((sum, item) => sum + +item.quantity, 0)
    },
    calculateTotalReturnAmount() {
      return this.form.items.reduce(
        (sum, item) => sum + this.calculateReturnAmount(item),
        0
      )
    }
  }
}
</script>

<style scoped></style>
